import { AxiosError, AxiosResponse } from 'axios';
import { get } from 'lodash';

import { createActionSet } from '../../helpers/actions';
import {
  ActionSet,
  FreeShapeAction,
  Meta
} from '../../types';
import { AXIOS_REQUEST } from './constants';
import {
  AxiosActionError,
  AxiosActionResponse,
  AxiosFlaggedAction,
  AxiosFlaggedMeta,
  AxiosFlaggedPayload
} from './types';

/**
 * Checks if the action is an axios flagged action.
 * @param {FreeShapeAction} action accepted middleware action.
 * @returns {boolean}
 */
export const isAxiosRequest = (action: FreeShapeAction): action is AxiosFlaggedAction => get(action, 'meta.flag') === AXIOS_REQUEST;

/**
 * Gets the request configuration from the action payload.
 * @param {AxiosFlaggedAction} action
 * @returns {AxiosFlaggedAction.payload.request}
 */
export const getRequestConfig = (action: AxiosFlaggedAction): AxiosFlaggedAction['payload']['request'] =>
  action.payload.request;

/**
 * Gets the client name from the action.
 * @param {AxiosFlaggedAction} action
 * @returns {AxiosFlaggedPayload.client}
 */
export const getClientName = (action: AxiosFlaggedAction): AxiosFlaggedPayload['client'] => action.payload.client;

/**
 * Removes some keys from the metadata cleaning the action.
 * @param {AxiosFlaggedMeta} meta
 * @returns {Meta}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const removeMetadata = ({ flag, ...filteredMeta }: AxiosFlaggedMeta): Meta => filteredMeta;

/**
 * Gets a pending action to be delivered.
 * @param {AxiosFlaggedAction} action
 * @returns {FreeShapeAction}
 */
export const getPendingAction = (action: AxiosFlaggedAction): FreeShapeAction => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.PENDING
  };
};

/**
 * Gets a fullifed action to be delivered.
 * @param {AxiosFlaggedAction} action
 * @param {AxiosResponse} response
 * @returns {ApiActionResponse}
 */
export const getFulfilledAction = (action: AxiosFlaggedAction, response: AxiosResponse): AxiosActionResponse => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.FULFILLED,
    payload: response
  };
};

/**
 * Gets a rejected action to be delivered.
 * @param {AxiosFlaggedAction} action
 * @param {AxiosError} error
 * @returns {ApiActionError}
 */
export const getRejectedAction = (action: AxiosFlaggedAction, error: AxiosError): AxiosActionError => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.REJECTED,
    payload: error,
    error: true
  };
};
